import { Memoize } from 'lodash-decorators'
import { get, trim } from 'lodash'
import moment from 'moment'
import { graph } from '@adc/constants'
import { images } from '../../img/exported'
import { absoluteToRelativeUrl } from '../../util/url'
import { cwcotSellerCodes } from '../utils/property'
import * as propertyConstants from '../constants'

const PURCHASE_AGREEMENT = 'Purchase Agreement'

class Property {
  constructor(property) {
    this._property = property
    this._property_details = this.isREO
      ? property.property_details
      : property.property_detail
  }

  static constants = propertyConstants

  get isREO() {
    return !this._property.is_trustee
  }

  get isTPSOnline() {
    return !this.isREO && !!get(this._property, 'is_tps_online', false)
  }

  get isTPSLive() {
    return !this.isREO && !get(this._property, 'is_tps_online', false)
  }

  get isTPSOhio() {
    return this.isTPSOnline && this.propertyState === 'Ohio'
  }

  get isFCLMarketing() {
    return (
      !this.isREO &&
      get(this._property_details, 'product_type_original') === 'FCL Marketing'
    )
  }

  get propertyId() {
    return this._property_details.global_property_id
  }

  get listingId() {
    return this._property_details.property_id
  }

  get propertyMorpheusId() {
    return this._property_details.property_morpheus_id
  }

  get pdpUrl() {
    return this._property_details.pdp_url
  }

  get soldDate() {
    return get(this._property_details, 'sold_date', '')
  }

  get relativePdpUrl() {
    return absoluteToRelativeUrl(this.pdpUrl)
  }

  get auctionNumber() {
    return this.isREO
      ? this._property.transaction_details.auction_number
      : this._property.auction.auction_number
  }

  get homeDisclosureCoverage() {
    return this.isREO
      ? get(this._property, 'transaction_details.home_disclosure_coverage', '')
      : !!this.documents.find(elem =>
          String(elem.display_label)
            .toLowerCase()
            .includes('property information report')
        )
  }

  get homeDisclosureUrl() {
    const PIR = this.documents.find(elem =>
      String(elem.display_label)
        .toLowerCase()
        .includes('property information report')
    )
    return this.isREO
      ? get(this._property, 'transaction_details.home_disclosure_url', '')
      : get(PIR, 'regular_url', '')
  }

  get propertyType() {
    return this._property_details.property_type
  }

  get propertyCity() {
    return this.isREO
      ? get(this._property_details, 'address.city')
      : get(this._property_details, 'property_city')
  }

  get propertyCounty() {
    return this.isREO
      ? get(this._property_details, 'address.county')
      : get(this._property_details, 'property_county')
  }

  get propertyState() {
    return this.isREO
      ? get(this._property_details, 'address.state')
      : get(this._property_details, 'property_state')
  }

  get propertyStateCode() {
    return this.isREO
      ? get(this._property_details, 'address.state_code')
      : get(this._property_details, 'property_state_code')
  }

  get propertyAddress() {
    return this.isREO
      ? get(this._property_details, 'address.complete_address')
      : get(this._property, 'address.full_property_address')
  }

  get streetName() {
    return this.isREO
      ? get(this._property_details, 'address.street_name', '')
      : get(this._property_details, 'property_address', '')
  }

  get propertyPostalCode() {
    return this.isREO
      ? get(this._property_details, 'address.postal_code', '')
      : get(this._property_details, 'property_zip', '')
  }

  get yearBuilt() {
    return get(this._property_details, 'year_built', '')
  }

  get agentInfo() {
    return get(this._property, 'transaction_details.agent_info[0]', {})
  }

  get closingCompanyData() {
    const {
      closing_company_alt_contact: name = '',
      closing_company_address: streetAddress = '',
      closing_company_city: city = '',
      closing_company_state: state = '',
      closing_company_alt_zip: zip = '',
      closing_company_alt_phone: phone = '',
      closing_company_alt_email: email = '',
    } = this._property_details
    return !this.isTPSOnline
      ? {}
      : {
          name,
          address:
            (streetAddress ? streetAddress + ', ' : '') +
            (city ? city + ', ' : '') +
            state +
            ' ' +
            zip,
          phone,
          email,
        }
  }

  get propertyImages() {
    return this.isREO
      ? get(this._property, 'images', []).map(image => image.regular_url)
      : get(this._property, 'document.images.documents', []).map(
          image => image.regular_url
        )
  }

  get paymentInformation() {
    return get(this._property, 'finance.msg_for_checks')
  }

  get coverImage() {
    return this.propertyImages[0] || images.defaultPropertyImage
  }

  get apn() {
    return this.isREO
      ? null
      : get(this._property_details, 'property_trustee_details.apn', '-')
  }

  get trusteeSaleNoLabel() {
    return this.isREO
      ? null
      : get(
          this._property_details,
          'property_trustee_details.trustee_sale_no_alt_text'
        ) || 'Foreclosure / Trustee No.'
  }

  get trusteeSaleNo() {
    const showTrusteeSaleNo = get(
      this._property_details,
      'property_trustee_details.display_trustee_sale_no',
      false
    )
    return !this.isREO && showTrusteeSaleNo
      ? get(
          this._property_details,
          'property_trustee_details.trustee_sale_number',
          null
        )
      : null
  }

  /**
   * A very component design component we use across this product is splitting the address across
   * two different lines for display in a component. This method returns the address in a format
   * that makes that process very simple.
   */

  @Memoize
  get addressAsTuple() {
    if (this.isREO) {
      const address = this._property_details.address

      return [
        `${get(address, 'street_number', '')} ${get(
          address,
          'street_name',
          ''
        )} ${get(address, 'unit_number', '')}`,
        `${get(address, 'city') ? get(address, 'city') + ',' : ''} ${get(
          address,
          'state',
          ''
        )} ${get(address, 'postal_code', '')}`,
      ]
    } else {
      const {
        property_address: propertyAddress = '',
        property_city: propertyCity = '',
        property_state: propertyState = '',
        property_zip: propertyZip = '',
      } = this._property_details
      return [
        propertyAddress,
        `${
          propertyCity ? propertyCity + ',' : ''
        } ${propertyState} ${propertyZip}`,
      ]
    }
  }

  get latLong() {
    if (this.isREO) {
      const address = this._property_details.address
      return {
        lat: parseFloat(get(address, 'latitude')),
        lng: parseFloat(get(address, 'longitude')),
      }
    } else {
      return {
        lat: parseFloat(this._property_details.latitude),
        lng: parseFloat(this._property_details.longitude),
      }
    }
  }

  get propertyDescription() {
    return get(this._property, 'property_details.property_description', '')
  }

  get auctioneerInfo() {
    return get(this._property, 'transaction_details.auctioneer_info', '')
  }

  get transactionDetailsInformation() {
    return get(
      this._property,
      'transaction_details.transaction_cms_information',
      ''
    )
  }

  get specialNotes() {
    return this.isTPSOhio
      ? get(this._property_details, 'special_notes', '')
      : get(this._property_details, 'mls_comment', '')
  }

  get startingBid() {
    return get(this._property, 'transaction_details.bid_info.starting_bid', '')
  }

  get estDebt() {
    return get(this._property, 'price_area.est_debt', null)
  }

  get originalBalance() {
    return get(this._property, 'price_area.original_balance', null)
  }

  get propertyOccupancyStatus() {
    return this._property_details.occupancy_status
  }

  get isVacant() {
    return this._property_details.occupancy_status === 'Vacant'
  }

  get productTypeOriginal() {
    return this._property.transaction_details.product_type_original
  }

  get sellerCode() {
    return this._property_details.seller_code
  }

  get parentSeller() {
    return this._property_details.parent_seller_company
  }

  get sellerClosingCompany() {
    return this._property_details.seller_closing_company
  }

  get contractClosingCompany() {
    return this._property_details.contract_closing_company
  }

  get openHouses() {
    return get(
      this._property,
      'transaction_details.open_house_showings',
      []
    ).filter(openHouse =>
      moment(openHouse.scheduled_date, 'YYYY-MM-DD').isValid()
    )
  }

  get hoaDues() {
    return this._property_details.hoa_dues
  }

  get additionalContacts() {
    return get(this._property, 'additional_contacts', {})
  }

  get appraiserLink() {
    return this.additionalContacts.appraiser_link
  }

  get clerkOfCourtLink() {
    return this.additionalContacts.clerk_of_court_link
  }

  get breadCrumbs() {
    if (this.isREO) {
      return get(this._property, 'additional_details.bread_crumbs', {})
    } else {
      const {
        state_data: state,
        county_data: county,
        city_data: city,
      } = get(this._property, 'breadcrumb', {})
      return {
        city,
        state,
        county,
      }
    }
  }

  get breadCrumbList() {
    const { city, state, county } = this.breadCrumbs
    return [
      { label: 'Auction.com', url: '/' },
      state,
      county,
      city,
      { label: this.streetName, url: this.relativePdpUrl },
    ]
  }

  get breadCrumbSchema() {
    // In some cases if we have incomplete data the breadcrumbs won't be as accurate. However, we guard here against
    // that because the ability to show all breadcrumbs shouldn't throw an exception in the consuming application. This
    // is best effort.
    const breadCrumbList = this.breadCrumbList.filter(Boolean)

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadCrumbList
        .slice(1, breadCrumbList.length - 1)
        .map(({ label, url }, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: label,
          item: `https://auction.com${url}`,
        })),
    }
  }

  get disclaimer() {
    return get(this._property, 'disclaimer.disclaimer_map.cms_body')
  }

  get showFannieMaeQuestion() {
    return this.parentSeller.includes('Fannie')
  }

  get showFreddieMacQuestions() {
    return this.parentSeller.includes('Freddie')
  }

  get statusCsv() {
    return this._property_details.status_csv
  }

  get isContractComplete() {
    return (
      this.auctionDayStatus ===
      Property.constants.AUCTION_DAY_STATUSES.CONTRACT_COMPLETE
    )
  }

  get isPendingSaleConfirmation() {
    const { SOLD, FINANCE_IN_PROGRESS, ESCROW_IN_PROGRESS } =
      Property.constants.AUCTION_DAY_STATUSES
    return [SOLD, FINANCE_IN_PROGRESS, ESCROW_IN_PROGRESS].includes(
      this.auctionDayStatus
    )
  }

  get isAcceptingDirectOffers() {
    return (
      this.isOfferEnabled &&
      !this.isContractComplete &&
      !this.isPendingSaleConfirmation
    )
  }

  get isSoldPresale() {
    return this.statusCsv === 'Sold Presale'
  }

  get auctionId() {
    return this.isREO
      ? get(this._property, 'transaction_details.auction_id', '0')
      : get(this._property, 'auction.auction_id', '0')
  }

  get venueId() {
    return this._property.transaction_details.venue_id
  }

  get venueCode() {
    return this._property.transaction_details.venue.venue_code
  }

  get afterRepairValue() {
    return this.isREO
      ? get(this._property_details, 'after_repair_value', '')
      : get(this._property, 'price_area.after_repair_value', '')
  }

  get auctionDate() {
    return this._property.transaction_details.auction_date
  }

  get brokerCoop() {
    return this._property.transaction_details.broker_co_op
  }

  get leaseInfo() {
    return get(this._property_details, 'lease_info', {})
  }

  get transactionDetails() {
    return this._property.transaction_details
  }

  get FAQLink() {
    return this.isREO
      ? '/lp/learn-more/frequently-asked-questions/'
      : get(this._property, 'additional_contacts.foreclosure_faq_url', '')
  }

  get isNonFinanceableAndRestrictedSellerCode() {
    // APA-294: Do not allow Hard Money Loan and Line Of Credit for non-financeable assets
    // from Chase/Flagstar/Shellpoint CWCOT seller codes
    const sellerCodeSet = new Set([
      'FG1',
      'FG2',
      'FG3',
      'FG4',
      'FOS',
      'FVS',
      'JHN',
      'JHV',
      'JN2',
      'JV1',
      'JV2',
      'SP6',
      'SP7',
    ])
    return !this.isFinancingAvailable && sellerCodeSet.has(this.sellerCode)
  }

  get isPotentialReturnAvailable() {
    return get(
      this._property,
      'property_return.is_potential_return_available',
      false
    )
  }

  get potentialReturn() {
    return get(this._property, 'property_return.potential_return', 0)
  }

  get eventId() {
    return get(this._property, 'transaction_details.event_id', null)
  }

  get redcid() {
    return this.isREO
      ? get(this._property, 'transaction_details.event_item', '')
      : get(this._property, 'product_details.event_item', '')
  }

  get statusLabel() {
    return this._property_details.status_label
  }

  get auctionDayStatus() {
    return this.isREO
      ? this._property.transaction_details.auction_day_status
      : this._property.auction.auction_day_status
  }

  get productAssetType() {
    const {
      ASSET_TYPE_LABELS,
      PRODUCT_ASSET_TYPE_BANK,
      PRODUCT_ASSET_TYPE_RENTAL,
      PRODUCT_ASSET_TYPE_SHORT_SALE,
      PRODUCT_ASSET_TYPE_TPS_ONLINE,
      PRODUCT_ASSET_TYPE_TPS_LIVE,
      PRODUCT_ASSET_TYPE_PRIVATE_SELLER_INSTITUTIONAL,
    } = Property.constants
    if (this.isREO) {
      const productType = this._property.transaction_details.product_type
      switch (productType) {
        case ASSET_TYPE_LABELS.default:
          return PRODUCT_ASSET_TYPE_BANK
        case ASSET_TYPE_LABELS.rentals:
          return PRODUCT_ASSET_TYPE_RENTAL
        case ASSET_TYPE_LABELS['short sale']:
          return PRODUCT_ASSET_TYPE_SHORT_SALE
        case ASSET_TYPE_LABELS['private seller institutional']:
          return PRODUCT_ASSET_TYPE_PRIVATE_SELLER_INSTITUTIONAL
        default:
          return PRODUCT_ASSET_TYPE_BANK
      }
    } else {
      if (
        this.isOnlineEvent ||
        this.listing?.listing_configuration?.is_third_party_online
      ) {
        return PRODUCT_ASSET_TYPE_TPS_ONLINE
      } else {
        return PRODUCT_ASSET_TYPE_TPS_LIVE
      }
    }
  }

  get saleDeposit() {
    return this._property_details.sale_deposit_amt
  }

  get winningBidTime() {
    return this._property.transaction_details.winning_bid_time
  }

  get winningBidAmount() {
    return this._property.property_details.winning_bid_amount
  }

  get isOnlineEvent() {
    return this.isREO
      ? this._property.transaction_details.online_event
      : this._property.auction.auction_details.online_event
  }

  get isLiveEvent() {
    return !this.isOnlineEvent
  }

  get titleInsurancePaidBy() {
    const titleInsurance = get(
      this._property,
      'listing.listing_configuration.title_insurance'
    )
    const {
      TITLE_INSURANCE_PAID_BY_BUYER,
      TITLE_INSURANCE_PAID_BY_SELLER,
      TITLE_INSURANCE_NOT_OFFERED,
      TITLE_INSURANCE_REFER_TO_PSA,
    } = Property.constants

    const titleInsurancePolicyLabels = {
      [graph.enums.ListingTitleInsurancePolicy.BUYER_TO_PAY]:
        TITLE_INSURANCE_PAID_BY_BUYER,
      [graph.enums.ListingTitleInsurancePolicy.SELLER_TO_PAY]:
        TITLE_INSURANCE_PAID_BY_SELLER,
      [graph.enums.ListingTitleInsurancePolicy.NOT_OFFERED]:
        TITLE_INSURANCE_NOT_OFFERED,
      [graph.enums.ListingTitleInsurancePolicy.REFER_TO_PSA]:
        TITLE_INSURANCE_REFER_TO_PSA,
    }
    return titleInsurancePolicyLabels[titleInsurance] || 'Unknown'
  }

  get showTitleInsuranceCheck() {
    return (
      this._property_details.title_insurance_paid_by ===
      Property.constants.TITLE_INSURANCE_PAID_BY_SELLER
    )
  }

  get sellerDeedType() {
    const sellerDeeds = (this._property_details.seller_deed_type || '')
      .split(',')
      .map(
        elem =>
          Property.constants.SELLER_DEED_CONVERSION_MAP[elem] || 'Not Available'
      )
    return sellerDeeds.join(' or ')
  }

  get sellerDeedTypeToolTip() {
    const {
      SELLER_DEED_CONVERSION_MAP,
      SELLER_DEED_TOOLTIP_CONVERSION_MAP,
      SPECIAL_WARRANTY_DEED,
      QUIT_CLAIM_DEED,
    } = Property.constants
    const sellerDeeds = (this._property_details.seller_deed_type || '')
      .split(',')
      .map(elem => SELLER_DEED_CONVERSION_MAP[elem] || 'Not Available')
    if (sellerDeeds.length === 1) {
      if (SELLER_DEED_TOOLTIP_CONVERSION_MAP[sellerDeeds[0]]) {
        return SELLER_DEED_TOOLTIP_CONVERSION_MAP[sellerDeeds[0]]
      }
    } else if (sellerDeeds.length === 2) {
      let specialWarrantyDeed = false
      let quitClaimDeed = false
      sellerDeeds.forEach(sellerDeed => {
        if (sellerDeed === SPECIAL_WARRANTY_DEED) {
          specialWarrantyDeed = true
        } else if (sellerDeed === QUIT_CLAIM_DEED) {
          quitClaimDeed = true
        }
      })
      if (specialWarrantyDeed && quitClaimDeed) {
        return 'You have an option to buy title insurance and receive a Special Warranty Deed. If you do not buy title insurance, you will receive a Quitclaim Deed.'
      }
    }

    return null
  }

  get titleInsurancePaidByToolTip() {
    return this.titleInsurancePaidBy === 'Seller'
      ? Property.constants.TITLE_INSURANCE_TOOLTIP
      : null
  }

  get showHomeDisclosureReport() {
    return Boolean(
      this._property.transaction_details.home_disclosure_coverage &&
        (this.statusLabel.indexOf('For Sale') === 0 ||
          this.statusLabel === 'Coming Soon')
    )
  }

  get isFinancingAvailable() {
    return this._property.transaction_details.is_financing_available
  }

  get bedCount() {
    return this.isREO
      ? this._property_details.total_bedrooms
      : get(this._property, 'product_details.bedrooms', '')
  }

  get bathCount() {
    return this.isREO
      ? this._property_details.total_bathrooms
      : get(this._property, 'product_details.bathrooms', '')
  }

  @Memoize
  toTrackingContext() {
    return {
      'property.id': this.propertyId,
      'event.id': this.auctionNumber,
      'property.type': this.propertyType,
      'property.city': this.propertyCity,
      'property.county': this.propertyCounty,
      'property.state': this.propertyState,
      'property.address': this.propertyAddress,
      'property.occupancyStatus': this.propertyOccupancyStatus,
      productTypeOriginal: this.productTypeOriginal,
      sellerCode: this.sellerCode,
      startingBid: this.startingBid,
      statusLabel: this.statusLabel,
      'auction.id': this.auctionId,
      'auction.type': 'online',
      'venue.id': this.venueId,
      'asset.type': 'reo',
      lob: 'reo',
      'auction.date': this.auctionDate,
      'property.status': this.statusLabel,
      'property.equityNumber': this.potentialReturn,
    }
  }

  get belongsToCwcotSeller() {
    return cwcotSellerCodes.has(this.sellerCode)
  }

  get documents() {
    return this.isREO
      ? get(this._property, 'documents', [])
      : get(this._property, 'document.sortedDocs', []).filter(doc =>
          Boolean(doc.regular_url)
        ) // Need to filter out invalid documents
  }

  get specialDocuments() {
    const documents = this.documents
    return {
      titleReport: documents.find(
        elem =>
          String(elem.display_label)
            .toLowerCase()
            .includes('title information') ||
          String(elem.doc_type) ===
            graph.enums.MediaPurposeType.PRELIMINARY_TITLE_REPORT
      ),
      sampleCertOfSale: documents.find(
        elem =>
          String(elem.doc_type).toLowerCase().includes('cert of sale') ||
          String(elem.doc_type) ===
            graph.enums.GlobalMediaPurpose.SAMPLE_TRUSTEE_CERT_OF_SALE
      ),
      noticeOfSale: documents.find(elem =>
        String(elem.display_label).toLowerCase().includes('notice of sale')
      ),
      sampleDeed: documents.find(
        elem =>
          String(elem.doc_type).toLowerCase().includes('sample deed') ||
          String(elem.doc_type) === graph.enums.GlobalMediaPurpose.SAMPLE_DEED
      ),
      purchaseAgreement: documents.find(
        elem =>
          String(elem.display_label)
            .toLowerCase()
            .includes('purchase agreement') ||
          String(elem.doc_type) ===
            graph.enums.GlobalMediaPurpose.PURCHASE_AGREEMENT
      ),
      addendum: documents.find(
        elem =>
          String(elem.display_label).toLowerCase().includes('addendum') ||
          String(elem.doc_type) ===
            graph.enums.GlobalMediaPurpose.PURCHASE_AGREEMENT_ADDENDUM
      ),
      additionalPropertyInfo: documents.find(elem =>
        String(elem.display_label)
          .toLowerCase()
          .includes('additional property information')
      ),
      propertyInspectionReport: documents.find(elem =>
        String(elem.display_label)
          .toLowerCase()
          .includes('property inspection report')
      ),
      termsAndConditions:
        this.isTPSOhio &&
        documents.find(
          elem =>
            String(elem.display_label)
              .toLowerCase()
              .includes('terms and conditions') ||
            String(elem.doc_type) ===
              graph.enums.GlobalMediaPurpose.TERMS_AND_CONDITIONS
        ),
      legalDescription:
        this.isTPSOhio &&
        documents.find(elem =>
          String(elem.display_label).toLowerCase().includes('legal description')
        ),
    }
  }

  get purchaseAgreement() {
    return this.documents.find(
      document => document.display_label === PURCHASE_AGREEMENT
    )
  }

  get closingDate() {
    return this._property_details.actual_close_date
  }

  get showLeaseInfo() {
    const productType = trim(
      this._property.transaction_details.product_type
    ).toLowerCase()
    return ['rental', 'mtl', 'income producing'].includes(productType)
  }

  get badges() {
    return this.isREO
      ? get(this._property, 'additional_details.new_badges', [])
      : get(this._property, 'new_badges', [])
  }

  get exteriorAcreage() {
    return this.isREO
      ? get(this._property_details, 'exterior_acerage', '')
      : get(this._property, 'product_details.lot_size', '')
  }

  get isSubjectToRedemption() {
    return this.badges.some(badge => badge.key === 'subject_to_redemption')
  }

  get isOfferEnabled() {
    return this._property_details.is_offer_enabled
  }

  get squareFootage() {
    return this.isREO
      ? this._property_details.interior_square_footage
      : get(this._property, 'product_details.square_footage', '')
  }

  get isHotProperty() {
    return this.isPotentialReturnAvailable && this.potentialReturn >= 3
  }

  toSavePropertyBody() {
    return {
      global_property_id: this.propertyId,
      property_id: this.listingId,
      property_morpheus_id: this.propertyMorpheusId,
      property_address: this.propertyAddress,
      auction_id: this.auctionId,
    }
  }

  toMSRealtyTracBody() {
    return {
      full_address: this.streetName,
      city: this.propertyCity.toUpperCase(),
      state: this.propertyState.toUpperCase(),
      zip_code: this.propertyPostalCode,
      global_property_id: this.propertyId,
    }
  }

  get listing() {
    return get(this._property, 'listing', {})
  }

  @Memoize
  get marketingData() {
    return {
      sellerCode: this.sellerCode,
      feature9: this._property_details.property_feature9,
      eventCode: this.auctionNumber,
    }
  }

  hasSellerCodes(sellerCodes) {
    return sellerCodes.includes(this.sellerCode)
  }
}

export default Property
